import {Component} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet} from '@angular/router';
import {filter} from 'rxjs';
import {Spinner} from '@core/components';
import {AuthStateService} from '@auth/states';
import {AppStateService} from '@core/states';
import {ModalHost} from '@core/modals';
import {ErrorService, LoadingService} from '@core/services';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    Spinner,
    ModalHost
  ],
  template: `@if (!appState.isOnline()) {
    <div class="fixed top-0 left-0 right-0 z-110 bg-amber-100 text-amber-900 text-center py-2 cd-text-sm">
      Sin conexion. Algunas acciones pueden no funcionar.
    </div>
  }
  @if (errorService.status() === 400 || errorService.status() === 401 || errorService.status() === 422) {
    <div class="fixed top-0 left-0 right-0 z-110 bg-red-50 text-red-900 text-center py-2 px-4 cd-text-sm flex items-center justify-center gap-2">
      <span>{{ errorService.message() }}</span>
      @if (errorDetailMessage) { <span>- {{ errorDetailMessage }}</span> }
      <button (click)="clearError()" class="btn btn-primary-outline cd-text-xs" style="padding: 0.1rem 0.4rem">
        Cerrar
      </button>
    </div>
  }
  @if (isLoading) {
    <div
      class="bg-gray-50 dark:bg-gray-900 fixed inset-0 overflow-y-auto flex-col h-screen w-screen z-200 backdrop-blur-xs flex items-center justify-center gap-5">
      <core-spinner class="w-10"/>
      <h1 class="text-2xl text-white">{{ appName }}</h1>
    </div>
  }
  <router-outlet></router-outlet>
  <core-modal-host></core-modal-host>
  <div class="absolute top-1 right-1 flex gap-2 align-center">
    @if (appState.isDev() && authState.isAuthenticated()) {
      <button (click)="logout()" class="btn btn-primary-outline cd-text-xs" style="padding: 0.25rem 0.5rem">
        Logout (Dev)
      </button>
    }
    <div class="outline cd-text-sm py-1 text-center my-auto {{ appState.isOnline() ? 'success' : 'error'}}"
         style="padding: 0 !important; width: 5.6rem">
      <span>{{ appState.isOnline() ? 'Conectado' : 'Desconectado' }}</span>
    </div>
  </div>
  `,
})
export class App {
  routerLoading = false;
  externalLoading = false;
  private routerLoadingTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly routerLoadingDelayMs = 250;

  appName: string = 'Compliance Desk'

  constructor(
    protected authState: AuthStateService,
    protected appState: AppStateService,
    private router: Router,
    private loading: LoadingService,
    protected errorService: ErrorService
  ) {
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart ||
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError)
      )
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.errorService.clear();
          if (!this.routerLoadingTimer) {
            this.routerLoadingTimer = setTimeout(() => {
              this.routerLoading = true;
              this.routerLoadingTimer = null;
            }, this.routerLoadingDelayMs);
          }
        } else if (event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError) {
          if (this.routerLoadingTimer) {
            clearTimeout(this.routerLoadingTimer);
            this.routerLoadingTimer = null;
          }
          this.routerLoading = false;
        }
      });

    this.loading.loading$.subscribe(isLoading => {
      this.externalLoading = isLoading;
    });
  }

  get isLoading(): boolean {
    return this.routerLoading || this.externalLoading;
  }

  get errorDetailMessage(): string | null {
    const details: any = this.errorService.details();
    if (Array.isArray(details) && details.length) {
      return details[0]?.message || details[0]?.detail || null;
    }
    if (details?.violations && Array.isArray(details.violations) && details.violations.length) {
      return details.violations[0]?.message || null;
    }
    return null;
  }

  clearError() {
    this.errorService.clear();
  }

  logout() {
    this.authState.logout();
  }
}
